import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from './cn';
import { Spinner } from './spinner';

/**
 * An empty view is an invitation to act: say what is missing and offer the action. `boxed` (the
 * default) frames a page's empty zone; `plain` sits centred inside a panel or a list that already
 * has its frame.
 */
export function EmptyState({
  icon,
  title,
  children,
  action,
  variant = 'boxed',
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  variant?: 'boxed' | 'plain';
  className?: string;
}) {
  const plain = variant === 'plain';
  return (
    <div
      className={cn(
        'flex flex-col gap-2.5',
        plain
          ? 'min-w-0 items-center p-6 text-center'
          : 'max-w-md items-start rounded-crow-panel border border-dashed border-crow-line-strong p-7',
        className,
      )}
    >
      {icon ? (
        <span aria-hidden className="text-crow-text-3 [&_svg]:size-6 [&_svg]:stroke-[1.5]">
          {icon}
        </span>
      ) : null}
      <p
        className={cn(
          'font-semibold break-words text-crow-text',
          plain ? 'text-crow-body' : 'text-crow-title',
        )}
      >
        {title}
      </p>
      {children ? (
        <div
          className={cn(
            'break-words text-crow-text-2',
            plain ? 'max-w-sm text-crow-compact' : 'text-crow-body',
          )}
        >
          {children}
        </div>
      ) : null}
      {action ? (
        <div className={cn('mt-1', plain && 'flex flex-wrap justify-center gap-2')}>{action}</div>
      ) : null}
    </div>
  );
}

/** A view or a section still loading: the spinner and what it waits for, announced once. */
export function LoadingState({
  children = 'Chargement…',
  className,
}: {
  children?: ReactNode;
  className?: string;
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex items-center justify-center gap-2.5 py-8 text-crow-body text-crow-text-2',
        className,
      )}
    >
      <Spinner className="size-4" />
      <span>{children}</span>
    </div>
  );
}

const banners = {
  danger: {
    icon: XCircle,
    box: 'border-[color-mix(in_oklch,var(--crow-danger)_28%,var(--crow-surface))] bg-crow-danger-tint',
    ink: 'text-crow-danger',
  },
  warning: {
    icon: AlertTriangle,
    box: 'border-[color-mix(in_oklch,var(--crow-warning)_30%,var(--crow-surface))] bg-[color-mix(in_oklch,var(--crow-warning)_9%,var(--crow-surface))]',
    ink: 'text-crow-warning',
  },
  info: {
    icon: Info,
    box: 'border-[color-mix(in_oklch,var(--crow-companion)_28%,var(--crow-surface))] bg-crow-companion-tint',
    ink: 'text-crow-companion-ink',
  },
  success: {
    icon: CheckCircle2,
    box: 'border-[color-mix(in_oklch,var(--crow-success)_28%,var(--crow-surface))] bg-[color-mix(in_oklch,var(--crow-success)_8%,var(--crow-surface))]',
    ink: 'text-crow-success',
  },
} as const;

/**
 * A problem that lasts and blocks part of the view. Say what went wrong and how to fix it, with
 * the fix as an action.
 */
export function Banner({
  tone = 'danger',
  title,
  children,
  actions,
  className,
}: {
  tone?: keyof typeof banners;
  /** Optional: a one-line message can go in children alone. */
  title?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  className?: string;
}) {
  const { icon: Icon, box, ink } = banners[tone];
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(
        'flex items-start gap-3 rounded-crow-panel border px-3.5 py-3 text-crow-body',
        box,
        className,
      )}
    >
      <Icon aria-hidden className={cn('mt-0.5 size-[18px] shrink-0', ink)} strokeWidth={1.75} />
      <div className="min-w-0 flex-1">
        {title ? <p className="font-semibold text-crow-text">{title}</p> : null}
        {children ? (
          <div className={title ? 'mt-0.5 text-crow-text-2' : 'text-crow-text'}>{children}</div>
        ) : null}
        {actions ? <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div> : null}
      </div>
    </div>
  );
}
